/* eslint-disable react/no-unstable-nested-components */
import WorkshopCaixaPretaFundamentei from "components/advertisements/WorkshopCaixaPretaFundamentei";
import type { IAuthContext } from "components/auth/Auth";
import { AuthContext } from "components/auth/Auth";
import OverdueSubscriptionStickyBanner from "components/billing/OverdueSubscriptionStickyBanner";
import Landing from "components/landing/Landing";
import NavbarLayout from "components/layouts/NavbarLayout";
import BackToTop from "components/qol/BackToTop";
import Screener from "components/screener/Screener";
import { getInitialSearchStateFromQuery } from "components/screener/searchState";
import TwoPassRender from "components/shared/TwoPassRender";
import WholePageLoadingBlankslate from "components/shared/WholePageLoadingBlankslate";
import { isWithinInterval, parseISO } from "date-fns";
import indexes from "lib/algolia/indexes";
import searchClient from "lib/algolia/searchClient";
import { SearchStateConsumer } from "lib/algolia/SearchStateContext";
import { uniq } from "lodash";
import type { NextPage } from "next";
import Head from "next/head";
import type { ReactNode } from "react";
import { useContext, useState } from "react";
import { findResultsState } from "react-instantsearch-dom/server";

interface IIndexProps {
  indexName: string;
  resultsState?: any;
}

const Index: NextPage<IIndexProps> = function Index({ indexName, resultsState }) {
  const { user, loading } = useContext<IAuthContext>(AuthContext);
  if (loading) {
    return <WholePageLoadingBlankslate />;
  }

  if (!user) {
    return <Landing />;
  }

  return (
    <NavbarLayout>
      <Head>
        <title>Fundamentei</title>
        {/* <meta name="viewport" content="width=1380, initial-scale=1, maximum-scale=1" key="viewport" /> */}
      </Head>

      <BackToTop />

      <OverdueSubscriptionStickyBanner />

      <CaixaPretaPopUps
        currentDate={new Date()}
        popUps={[
          {
            key: "liveComoMonteiAMinhaCarteira",
            title: (
              <h2 className="inline-flex items-center space-x-2 rounded-md bg-black/20 p-1 px-2 font-bold leading-6 text-white md:text-xl md:leading-normal">
                <span>Live | Como Montei a Minha Carteira</span>
                <span className="relative flex h-2 w-2 md:h-3 md:w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-80" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500 md:h-3 md:w-3" />
                </span>
              </h2>
            ),
            popUpContent: (
              <p>
                Fala Investidor! Gostaria de te convidar para uma Live inédita na próxima segunda, dia{" "}
                <strong className="inline-block rounded-md bg-black/50 p-1 px-2 text-xs text-white">
                  01/08 às 20h
                </strong>{" "}
                no Youtube, sobre como montei a minha carteira de Renda Variável, está imperdível! 😉
              </p>
            ),
            externalLinkText: "Definir Lembrete",
            externalLinkHref: "https://www.youtube.com/watch?v=ZdRIJtPVAa0",
            period: [parseISO("2022-07-26T22:00:00"), parseISO("2022-08-01T22:00:00")],
            rootClassName: "bg-gradient-to-r from-[#3f5fe9] to-red-500/90",
          },
          {
            key: "caixaPretaInscriçõesAbertas",
            title: (
              <h2 className="inline-flex items-center space-x-2 rounded-md bg-black/20 p-1 px-2 font-bold leading-6 text-white md:text-xl md:leading-normal">
                <span>Caixa Preta | Inscrições Abertas</span>
                <span className="inline-flex animate-pulse">🔥</span>
              </h2>
            ),
            popUpContent: (
              <p>
                Quer descobrir cada ação, FII, Stock e REIT que compõe a minha carteira e se sentir mais seguro com a
                escolha dos seus ativos? Então garanta já a sua vaga no Caixa Preta Fundamentei!
              </p>
            ),
            externalLinkText: "Ver mais detalhes",
            externalLinkHref: "https://hotm.art/YJqEjC6V",
            period: [parseISO("2022-08-01T22:00:01"), parseISO("2022-08-04T23:59:59")],
            rootClassName: "bg-gradient-to-r from-black/70 to-orange-400/100",
          },
          {
            key: "caixaPretaÚltimosDias",
            title: (
              <h2 className="inline-flex items-center space-x-2 rounded-md bg-black/20 p-1 px-2 font-bold leading-6 text-white md:text-xl md:leading-normal">
                <span>Caixa Preta | Últimos Dias</span>
                <span className="inline-flex animate-pulse">⚠️</span>
              </h2>
            ),
            popUpContent: (
              <p>
                Últimos dias para você descobrir cada ação, FII, Stock e REIT que compõe a minha carteira de renda
                variável e se sentir mais seguro com a escolha dos seus ativos... Corre que tá acabando!
              </p>
            ),
            externalLinkText: "Quero a minha vaga!",
            externalLinkHref: "https://hotm.art/YJqEjC6V",
            period: [parseISO("2022-08-05T00:00:00"), parseISO("2022-08-07T22:00:00")],
            rootClassName: "bg-gradient-to-r from-black/70 to-red-500/100",
          },
          {
            key: "blackFridayCaixaPretaComDesconto",
            title: (
              <h2 className="inline-flex items-center space-x-2 rounded-md bg-orange-600 p-1 px-2 font-bold leading-6 text-white md:text-xl md:leading-normal">
                <span>
                  Black Friday Caixa Preta <span className="underline">com desconto</span>!
                </span>
              </h2>
            ),
            popUpContent: (
              <p>
                Quer descobrir cada Ação, FII, Stock e REIT que compõe a minha carteira? Então garanta já a sua vaga com
                25% de desconto no Caixa Preta Fundamentei! <strong>Esse valor nunca mais vai se repetir</strong>...
              </p>
            ),
            externalLinkText: "Quero a minha vaga!",
            externalLinkHref: "https://hotm.art/YJqEjC6V",
            period: [parseISO("2022-11-25T22:00:00"), parseISO("2022-11-27T22:00:00")],
            rootClassName: "bg-gradient-to-r from-black/50 to-black/100",
          },
        ]}
      />

      <SearchStateConsumer>
        {({ searchState, setSearchState }) => (
          <Screener
            searchClient={searchClient}
            indexName={indexName}
            searchState={searchState}
            resultsState={resultsState}
            onSearchStateChange={setSearchState}
          />
        )}
      </SearchStateConsumer>
    </NavbarLayout>
  );
};

Index.getInitialProps = async ({ query }) => {
  const indexName = indexes.issuers;
  const searchState = getInitialSearchStateFromQuery(query);

  const resultsState = await findResultsState(Screener, {
    searchClient,
    indexName,
    searchState,
  });

  return {
    indexName,
    resultsState,
  };
};

export default Index;

interface ICaixaPretaPopUp {
  key: string;
  title: ReactNode;
  popUpContent: ReactNode;
  externalLinkText: string;
  externalLinkHref: string;
  rootClassName?: string;
  period: [Date, Date];
}

interface ICaixaPretaPopUpsProps {
  currentDate: Date;
  popUps: ICaixaPretaPopUp[];
}

const CAIXA_PRETA_POPUPS_KEY = "@fundamentei/caixaPretaPopUps";
function CaixaPretaPopUps({ currentDate, popUps }: ICaixaPretaPopUpsProps) {
  const [closedPopUps, setClosedPopUps] = useState<string[]>(() => {
    if (typeof localStorage !== "undefined") {
      const caixaPretaPopUps = localStorage.getItem(CAIXA_PRETA_POPUPS_KEY);
      if (!caixaPretaPopUps) {
        return [];
      }
      try {
        return JSON.parse(caixaPretaPopUps) || [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const popUp = popUps.find(({ period }) => {
    const [start, end] = period;
    return isWithinInterval(currentDate, {
      start,
      end,
    });
  });

  if (!popUp) {
    return null;
  }

  const hasClosedPopUp = closedPopUps.includes(popUp.key);
  return (
    <TwoPassRender>
      {() => {
        if (hasClosedPopUp) {
          return null;
        }
        return (
          <div className="m-auto mt-8 mb-0 max-w-[1140px] space-y-8">
            <WorkshopCaixaPretaFundamentei
              popUpTitle={popUp.title}
              className={popUp.rootClassName}
              externalLinkText={popUp.externalLinkText}
              externalLinkHref={popUp.externalLinkHref}
              onClose={() => {
                setClosedPopUps((closedPopUps) => {
                  const nextClosedPopUps = uniq(closedPopUps.concat(popUp.key));
                  localStorage.setItem(CAIXA_PRETA_POPUPS_KEY, JSON.stringify(nextClosedPopUps));
                  return nextClosedPopUps;
                });
              }}
            >
              {popUp.popUpContent}
            </WorkshopCaixaPretaFundamentei>
          </div>
        );
      }}
    </TwoPassRender>
  );
}
